import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from "react";

const INTERACTIVE_SELECTOR =
  "button, a, input, textarea, select, label, [role='button'], [role='menuitem']";

export type DraggingScrollResult = {
  ref: RefObject<HTMLDivElement | null>;
  onMouseDown: (e: ReactMouseEvent<HTMLDivElement>) => void;
  style: CSSProperties;
  isDraggingScroll: boolean;
};

/**
 * Click-drag horizontal scroll for overflow tables (same pattern as kobymandell Users).
 */
export function useDraggingScroll(): DraggingScrollResult {
  const [isDraggingScroll, setIsDraggingScroll] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragStartXRef = useRef(0);

  useEffect(() => {
    if (!isDraggingScroll) return;
    const el = scrollContainerRef.current;
    if (!el) return;

    const onMouseMove = (e: MouseEvent) => {
      const dx = dragStartXRef.current - e.clientX;
      el.scrollLeft += dx;
      dragStartXRef.current = e.clientX;
    };

    const onMouseUp = () => {
      setIsDraggingScroll(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDraggingScroll]);

  const handleMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    dragStartXRef.current = e.clientX;
    setIsDraggingScroll(true);
  }, []);

  return {
    ref: scrollContainerRef,
    onMouseDown: handleMouseDown,
    isDraggingScroll,
    style: {
      cursor: isDraggingScroll ? "grabbing" : "grab",
      userSelect: isDraggingScroll ? "none" : undefined,
    },
  };
}
